'use strict';
/* ==========================================================================
   levels/cases.js — step "cases" (orientation any): CASE FILES (DESIGN.md §5.2, look §2).

   Six case files per attempt from content/bank.cases.json through the model: Narration.attempt('cases', json) →
   Narration.drawCases (≥ 2 reliable, one of each unreliable kind, ≤ 1 public-domain excerpt, easiest first; the seed is
   saved in Game.progress.bank.cases, so a reload resumes the same files). Each file is an index card pinned to the
   corkboard: the case title, where the passage comes from ("a story written for this lesson" / the PD work), and its
   3–6 sentences, numbered.
   Per case:  drop → trust: Part A "Can you trust this narrator?" (3 options from the model: the right verdict + two
                     traps). A wrong pick is struck out with its remediation note (item.whyNot / the model's); pick again.
              → tap:   Part B (hot text) "Tap the sentence that gives the narrator away." (unreliable) or "... what this
                     narrator CAN'T know." (reliable, limited). A wrong sentence is crossed out with the model's why;
                     after 2 misses Ms. Park pins the clue herself.
              → close: a red string runs from the sentence to its evidence tag (the clue's name and colour from
                     Narration.CLUES; reliable: a CAN'T KNOW tag with what the narrator can't know), the other clue
                     sentences get their own highlights (+ tags and strings on the landscape board), the RELIABLE /
                     UNRELIABLE stamp comes down, and Ms. Park's note (item.explain, text only: no voice) → next case.
   Whether a pick or a tap is right is decided by the model (trust.answerIndex, tap.answers), never by a content field.
   Stars: right verdict first try = 1 + right sentence first try = 1. of 12, pass = json.passStars (8).
   Game.progress.scores.cases = {best, last, of}; first answers under answers['cases/<id>/trust'] and ['cases/<id>/tap'];
   Narration.finish at the end; the stamp (Game.complete) through "Collect your stamp" (a pass that leaves keeps it).
   Resume: Game.progress.bank.cases.run = {i, ev, res}: the current case's picks and taps are replayed.

   Portrait phone (Dock.active): the stage shows the case card large (a picture, with the evidence tags in a strip at the
   card's foot); the options are Dock.choices, the sentences Dock.choices rows (the hot text), the close a list.
   Landscape phone: the sentences / options are canvas tap targets ≥ 44 CSS px (UI.tap), text through UI.text.
   Keys: 1–3 = option, 1–6 = sentence, Enter / Space = next / start.

   QA hooks: sc.phase ('intro' | 'drop' | 'trust' | 'tap' | 'close' | 'end'), sc.R (the Narration case), sc.choose(i),
   sc.tap(i), sc.next(), sc.begin(fresh), sc.collect(), sc.back(), sc.done(); sc.cases, sc.bi, sc.results, sc.stars,
   sc.seed, sc.attempt, sc.resumed, sc.picks, sc.tapMarks, sc.tapMisses, sc.hitI, sc.revealed; Levels.cases.solution
   ({reliable, kind, trust, tap, answers}), Levels.cases.targets() (the live canvas tap rects: sentences + options).
   finishStub(stars) records the score and stamps the step.
   ========================================================================== */
(function () {
  const STEP = 'cases', MAX_TAP_MISSES = 2, DROP = 0.7, NEXT_GUARD = 0.3;
  const Z = () => window.Zine;
  const json = () => (Game.content && Game.content.bank_cases) || { pool: STEP, draw: { n: 6 }, items: [] };
  const TYPE = "'Special Elite', 'Courier New', monospace", HAND = "'Caveat Brush', cursive";
  const PARK = '#2b4a8c', RED = '#d9352c', GREY = '#6d6a64', INK = '#141210', YEL = '#fff04a', GREEN = '#2f8f3a', LIMIT = '#9fd3ff';
  const prog = (t0, d) => Math.max(0, Math.min(1, (T - t0) / d));
  const fnt = (s, f) => `${Math.round(s)}px ${f}`;
  const wrapF = (s, maxW, f) => UI._wrap(s, maxW, f);
  const q10 = v => Math.ceil(v / 10) * 10;

  const sourceLine = R => {
    const s = R.source;
    return s && typeof s === 'object' ? `from “${s.work}” by ${s.author} (${s.year})` : 'a story written for this lesson';
  };
  const verdictId = R => (R.verdict.reliable ? 'reliable' : R.verdict.kind);
  const verdictLabel = R => R.trust.options[R.trust.answerIndex].label;
  /* the evidence tag of sentence i: a clue (unreliable) or CAN'T KNOW (reliable: what the narrator admits it can't know) */
  function tagOf(R, i) {
    if (R.verdict.reliable) {
      const s = R.item && R.item.sentences && R.item.sentences[i];
      return { id: null, text: 'CAN\'T KNOW', color: LIMIT, sub: (s && s.limit) || '' };
    }
    const id = R.tap.clueOf[i];
    return { id, text: Narration.name(id).toUpperCase(), color: Narration.colour(id), sub: '' };
  }
  /* every sentence the close pins: the primary one first, then the other clue sentences (unreliable) */
  function pinned(R, primary) {
    const rest = R.verdict.reliable ? [] : R.tap.answers.filter(i => i !== primary);
    return [primary].concat(rest).sort((a, b) => a - b);
  }
  const tagW = (tg, size) => Math.round(Z().measure(tg.text, size) + size * 2.6);
  /* an evidence tag, centre x, y: a clue id through Zine.label; CAN'T KNOW drawn here (an eye doodle on blue paper) */
  function tag(tg, x, y, size, { s = 1, rot = 0, seed = 1 } = {}) {
    if (s <= 0.001) return;
    if (tg.id) { Z().label(tg.id, x, y, { size, rot, s, seed }); return; }
    const w = tagW(tg, size), h = Math.round(size * 1.5);
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 0, w, h, tg.color, { seed, depth: 0.7, boil: false });
    Z().doodle('first-limited', -w / 2 + size * 0.95, 0, size * 0.42, { seed: seed + 3, w: Math.max(2, size * 0.08) });
    Z().type(tg.text, -w / 2 + size * 1.75, size * 0.04, size, { seed });
    g.restore();
  }
  function cross(x, y, r, o = {}) {
    ink([[x - r, y - r], [x + r, y + r]], { w: o.w || 5, color: o.color || RED, seed: o.seed || 3, prog: o.prog == null ? 1 : o.prog, passes: 1, step: 8 });
    ink([[x + r, y - r], [x - r, y + r]], { w: o.w || 5, color: o.color || RED, seed: (o.seed || 3) + 1, prog: o.prog == null ? 1 : Math.max(0, o.prog * 2 - 1), passes: 1, step: 8 });
  }
  function tick(x, y, s, o = {}) { ink([[x - 16 * s, y], [x - 4 * s, y + 13 * s], [x + 20 * s, y - 18 * s]], { w: 6 * s, color: o.color || GREEN, seed: o.seed || 3, prog: o.prog == null ? 1 : o.prog, passes: 1, step: 6 }); }

  /* ---------------- the case card layout (mode 'm' mouse, 't' touch landscape, 'd' the docked picture) ---------------- */
  function layCase(R, mode) {
    const d = mode === 'd', t = mode === 't';
    const X = d ? 960 : 652, W = d ? 1860 : 1224;
    const top = d ? 14 : 118, bottom = d ? 1066 : 1064, H = bottom - top, Y = top + H / 2;
    const left = X - W / 2 + (d ? 118 : 100), right = X + W / 2 - (d ? 46 : 40), maxW = right - left;
    const headS = d ? 54 : t ? Math.max(42, Math.round(UI.text(38))) : 40, srcS = d ? 34 : Math.round(UI.text(24));
    const headY = top + 40, ruleY = top + 74, srcY = ruleY + srcS * 0.55 + 14, start = srcY + srcS * 0.5 + (d ? 30 : 24);
    const tagS = d ? 42 : 0, tagY = bottom - 58, textBottom = d ? bottom - 128 : bottom - 26;
    const minRow = t ? UI.tap(64) : 0;
    let L = null;
    for (const k of (mode === 'm' ? [1.2, 1.1] : []).concat([1, 0.93, 0.86, 0.79, 0.72, 0.65, 0.58, 0.52])) {
      const ts = Math.round((d ? 60 : t ? UI.text(36) : 36) * k), lh = ts * 1.3, gapMin = ts * 0.45;
      const rows = R.sentences.map((s, i) => { const lines = Z().wrap(s.text, maxW, ts); return { i, s, lines, textH: lines.length * lh }; });
      const slot = r => Math.max(r.textH + gapMin, minRow);
      const avail = textBottom - start, total = rows.reduce((a, r) => a + slot(r), 0);
      if (total > avail && k > 0.52) continue;
      const extra = Math.max(0, Math.min(ts * 0.7, (avail - total) / rows.length));
      let y = start;
      const out = rows.map(r => {
        const h = slot(r) + extra, y0 = y; y += h;
        const ty = y0 + (h - r.textH) / 2;
        const lines = r.lines.map((s, n) => ({ s, y: ty + (n + 0.5) * lh, w: Z().measure(s, ts) }));
        const last = lines[lines.length - 1];
        return { i: r.i, s: r.s, lines, ts, lh, top: y0, h, cy: y0 + h / 2,
          anchor: { x: right + 18, y: last.y },
          hit: { x: (left - 84 + right + 10) / 2, y: y0 + h / 2, w: right + 10 - (left - 84), h: h - 2 } };
      });
      L = { mode, X, Y, W, H, top, bottom, left, right, headS, headY, ruleY, srcS, srcY, rows: out, tagS, tagY, panelX: t ? 1290 : 1294 };
      break;
    }
    return L;
  }

  class CaseFiles extends Scene {
    get n() { return this.cases ? this.cases.length : (json().draw || {}).n || 6; }
    get of() { return this.n * 2; }
    get passStars() { return Math.min(this.of, json().passStars || Math.ceil(this.of * 2 / 3)); }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }
    get solution() {
      const R = this.R; if (!R) return null;
      return { reliable: R.verdict.reliable, kind: R.verdict.kind, trust: R.trust.answerIndex, tap: R.tap.answers[0], answers: R.tap.answers.slice(), wrongTap: R.sentences.map((s, i) => i).find(i => !R.tap.answers.includes(i)) };
    }
    /* tools/integration.js (the stub era): a draw of n cases through the model */
    get preview() { return { cases: this.cases || Narration.drawCases(json(), { seed: Bank.seedOverride() != null ? Bank.seedOverride() : 1 }).items }; }

    enter() {
      this.phase = 'intro'; this.cases = null; this.R = null; this.bi = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map();
      Audio.play('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();              // an attempt in progress: straight back in
    }
    exit() { this.bankStamp(); }
    back() { Game.replace(new Levels.map()); }

    /* ---- the attempt ---- */
    begin(fresh = false) {
      this.bankStamp();
      const S = Game.progress.bank || {}, prev = S[STEP] && S[STEP].open ? S[STEP].run : null;
      const a = Narration.attempt(STEP, json(), { fresh });
      this.cases = a.items; this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.results = []; this.passed = false; this.collected = false;
      if (a.resumed && prev && this.restore(prev)) return;
      this.startCase(0);
    }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.cases.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.caseAt = Game.time - DROP - 1; this.phase = 'trust'; this.landed = true;
        this.quiet = true;
        for (const [k, v] of run.ev || []) { if (k === 'c') this.choose(v); else if (k === 't') this.tap(v); }
        this.quiet = false;
        if (this.tapAt != null) this.tapAt = Math.min(this.tapAt, Game.time);
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[cases] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.bi = i; this.R = this.cases[i]; this.lays.clear(); this._cc = null; this._pl = null;
      this.ev = []; this.picks = []; this.trustStar = 0; this.tapStar = 0; this.tapMarks = {}; this.tapMisses = 0;
      this.hitI = -1; this.revealed = false; this.say = null; this.landed = false;
      this.caseAt = Game.time; this.pickedAt = this.tapAt = this.closeAt = null;
    }
    startCase(i) {
      this.setup(i); this.phase = 'drop';
      this.save();
      this.sfx('whoosh', { gain: 0.3 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end') S.run = { i: this.bi, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    sfx(name, o) { if (!this.quiet && Audio.has && Audio.has(name)) Audio.play(name, o); }
    sfxAny(names, o) { const n = names.find(x => Audio.has && Audio.has(x)); if (n) this.sfx(n, o); }

    /* ---- Part A: can you trust this narrator? ---- */
    choose(i) {
      const R = this.R;
      if (this.phase === 'drop') this.phase = 'trust';
      if (this.phase !== 'trust' || !R || !(i >= 0 && i < R.trust.options.length) || this.picks.includes(i)) return false;
      this.ev.push(['c', i]);
      if (!this.picks.length) Game.progress.answer(STEP + '/' + R.key + '/trust', R.trust.options[i].label);   // first try only
      this.picks.push(i); this.pickedAt = Game.time;
      if (i === R.trust.answerIndex) {                                     // the model's verdict (Narration.reliability)
        this.trustStar = this.picks.length === 1 ? 1 : 0;
        this.phase = 'tap'; this.tapAt = Game.time + 0.45; this.say = null;
        this.sfxAny(['ding'], { gain: 0.5 });
      } else this.sfx('thunk', { gain: 0.4, rate: 0.9 });
      this.save();
      return true;
    }
    /* ---- Part B: the hot text ---- */
    tap(i) {
      const R = this.R;
      if (this.phase !== 'tap' || !R || !(i >= 0 && i < R.sentences.length) || this.tapMarks[i]) return false;
      this.ev.push(['t', i]);
      Game.progress.answer(STEP + '/' + R.key + '/tap', `${i + 1}. ${R.sentences[i].text}`);            // first try only
      if (R.tap.answers.includes(i)) {                                    // the model's clue / limit sentences
        this.tapStar = this.tapMisses === 0 ? 1 : 0; this.hitI = i; this.say = null;
        this.toClose();
      } else {
        this.tapMarks[i] = { at: Game.time }; this.tapMisses++;
        this.say = { text: R.tap.why(i) || 'Not that one. Look again.', at: Game.time };
        this.sfx('thunk', { gain: 0.4, rate: 0.8 });
        if (this.tapMisses >= MAX_TAP_MISSES) {
          this.tapStar = 0; this.hitI = R.tap.answers[0]; this.revealed = true;
          this.say = { text: 'Ms. Park pins the clue for you.', at: Game.time };
          this.toClose();
        }
      }
      this.save();
      return true;
    }
    toClose() {
      const R = this.R;
      this.phase = 'close'; this.closeAt = Game.time; this._cc = null;
      this.results[this.bi] = { id: R.id, key: R.key, title: R.title, verdict: verdictId(R), wrongPicks: this.picks.length - 1, tapMisses: this.tapMisses,
        revealed: this.revealed, hit: this.hitI, trustStar: this.trustStar, tapStar: this.tapStar, stars: this.trustStar + this.tapStar };
      if (!this.revealed) this.sfxAny(['ding'], { gain: 0.5 });
      this.sfxAny(['string_pull', 'scribble'], { gain: 0.4 });
    }
    next() {
      if (this.phase !== 'close' || Game.time - this.closeAt < NEXT_GUARD) return false;
      if (this.bi < this.cases.length - 1) this.startCase(this.bi + 1);
      else this.finish();
      return true;
    }
    finish() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP]; if (S) delete S.run;
      Narration.finish(STEP);                                                          // the next attempt draws new cases
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    done() {
      if (!this.cases) this.begin();
      if (this.phase !== 'end') this.finish();
      this.collected = true; Game.complete(STEP);
    }
    finishStub(stars = this.of) {
      if (this._stubbed) return; this._stubbed = true;
      if (this.cases && this.phase !== 'end') { const S = Game.progress.bank && Game.progress.bank[STEP]; if (S) delete S.run; Narration.finish(STEP); }
      const S = Game.progress.scores, prev = S[STEP] || {};
      S[STEP] = { best: Math.max(prev.best || 0, stars), last: stars, of: this.of };
      Game.progress.save();
      this.collected = true;
      Game.complete(STEP);
    }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new cases)', go: () => this.begin() }]
        : [{ label: 'Try again (new cases)', go: () => this.begin() }, { label: 'Back to the map', go: () => this.back() }];
    }

    update() {
      if (this.phase === 'drop' && Game.time - this.caseAt > DROP + 0.1) this.phase = 'trust';
      if (this.R && !this.landed && Game.time - this.caseAt > DROP * 0.7) { this.landed = true; this.sfxAny(['paper_flip', 'paper'], { gain: 0.45 }); this.sfxAny(['typewriter'], { gain: 0.35 }); }
      if (this.phase === 'close' && !this._stampSfx && Game.time - this.closeAt > 0.9) { this._stampSfx = true; this.sfxAny(['stamp_thump', 'stamp'], { gain: 0.6 }); }
      if (this.phase !== 'close') this._stampSfx = false;
    }
    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); }
      else if (this.phase === 'drop' || this.phase === 'trust') { if (n >= 1 && n <= 3) this.choose(n - 1); }
      else if (this.phase === 'tap') { if (n >= 1 && n <= 9) this.tap(n - 1); }
      else if (this.phase === 'close') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* the close's animation order: the tapped (or pinned) sentence first, then the other clues top to bottom */
    pinOrder(pins, i) { return i === this.hitI ? 0 : 1 + pins.filter(x => x !== this.hitI).indexOf(i); }

    /* ---------------- drawing ---------------- */
    lay(docked) {
      const mode = docked ? 'd' : UI.touch() ? 't' : 'm', key = mode + '|' + Math.round(UI.text(40)) + '|' + this.bi;
      let L = this.lays.get(key);
      if (!L) { L = layCase(this.R, mode); this.lays.set(key, L); }
      return L;
    }
    sub() {
      if (!this.cases || this.phase === 'intro' || this.phase === 'end') return '';
      return `case ${this.bi + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }
    draw() {
      const docked = UI.docked();
      Z().cork();
      UI.hud('Case Files', docked ? this.sub() : '');
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay(docked);
      const s = E.outBack(prog(this.caseAt, DROP), 1.1);
      g.save(); g.translate(0, (1 - s) * -1100); g.rotate((1 - s) * -0.04);
      this.drawCard(L, docked);
      g.restore();
      if (docked) return this.drawDock();
      if (this.phase === 'drop' || this.phase === 'trust') this.drawTrust();
      else if (this.phase === 'tap') this.drawTap();
      else this.drawClose(L);
    }

    /* the index card: header, source, the numbered sentences (tap targets in Part B), the close's highlights + stamp */
    drawCard(L, docked) {
      const Zn = Z(), R = this.R, { X, Y, W, H, top, left, right } = L;
      Zn.card(X, Y, W, H, { seed: 300 + this.bi * 7 + (this.seed || 0) % 5, lines: false, pin: false });
      Zn.pin(X - W / 2 + 32, top + 30); Zn.pin(X + W / 2 - 32, top + 30, { color: '#2b4a8c' });
      const t0 = this.caseAt + DROP * 0.6, hx = X - W / 2 + 66;
      Zn.type(`CASE ${this.bi + 1}: ${String(R.title).toUpperCase()}`, hx, L.headY, L.headS, { t0, cps: 45, seed: 300 + this.bi });
      Zn.type(sourceLine(R), hx, L.srcY, L.srcS, { color: GREY, seed: 310 + this.bi });
      const tapping = this.phase === 'tap' && !docked && T >= this.tapAt, close = this.phase === 'close';
      const pins = close ? pinned(R, this.hitI) : [], keys = UI.keys && this.phase === 'tap' && !docked;
      for (const row of L.rows) {
        const i = row.i, at = t0 + 0.2 + i * 0.15, wrong = !!this.tapMarks[i];
        // the hot text: hover shade, the tap
        let hover = false;
        if (tapping && !wrong) {
          const h = UI._hit('cf-s' + this.bi + '_' + i, row.hit.x, row.hit.y, row.hit.w, row.hit.h);
          hover = h.hover; if (h.clicked) this.tap(i);
        }
        if (hover) { g.save(); g.globalAlpha *= 0.3; g.fillStyle = YEL; g.fillRect(left - 90, row.top + 3, right - left + 100, row.h - 6); g.restore(); }
        // the close: the clue sentences in their tag colours (the primary one first)
        const pk = pins.indexOf(i), ord = this.pinOrder(pins, i);
        if (pk >= 0) {
          const tg = tagOf(R, i), ht = this.closeAt + (ord === 0 ? 0 : 0.8 + ord * 0.35);
          row.lines.forEach((ln, n) => Zn.highlight(left - 8, ln.y + 2, ln.w + 16, row.ts * 1.05, tg.color, { t0: ht + n * 0.18, dur: 0.3, seed: 700 + i * 5 + n, alpha: 0.8 }));
        }
        // rules under each line (an index card), the number, the words
        g.save(); g.strokeStyle = 'rgba(82,140,200,0.3)'; g.lineWidth = 1.5;
        row.lines.forEach(ln => { g.beginPath(); g.moveTo(left - 90, ln.y + row.ts * 0.62); g.lineTo(right + 10, ln.y + row.ts * 0.62); g.stroke(); });
        g.restore();
        const nx = left - (docked ? 60 : 52), ny = row.lines[0].y, nr = docked ? 30 : Math.max(22, row.ts * 0.62);
        inkCircle(nx, ny, nr, { w: 3.5, color: i === this.hitI && close ? RED : GREY, seed: 50 + i, passes: 1 });
        g.save(); g.font = fnt(nr * 1.2, HAND); g.fillStyle = i === this.hitI && close ? RED : GREY; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), nx, ny + 2); g.restore();
        row.lines.forEach((ln, k) => Zn.type(ln.s, left, ln.y, row.ts, { t0: at + k * 0.08, cps: 110, seed: 20 + i * 5 + k, color: wrong ? GREY : INK }));
        if (wrong) cross(nx, ny, nr * 0.75, { prog: prog(this.tapMarks[i].at, 0.25), seed: 60 + i, w: docked ? 7 : 5 });
        if (keys && !wrong && T >= this.tapAt) { g.save(); g.font = fnt(20, TYPE); g.fillStyle = GREY; g.textAlign = 'center'; g.fillText(`[${i + 1}]`, nx, ny + nr + 18); g.restore(); }
      }
      if (!close) return;
      // the strings and tags: docked = a strip of tags at the card's foot; landscape = the panel (drawClose)
      if (docked) {
        const size = L.tagS, gap = 40, tgs = pins.map(i => ({ i, tg: tagOf(R, i) }));
        const ws = tgs.map(x => tagW(x.tg, size)), tot = ws.reduce((a, b) => a + b, 0) + gap * (ws.length - 1);
        let x = X - tot / 2;
        tgs.forEach((x_, k) => {
          const ord = this.pinOrder(pins, x_.i), cx = x + ws[k] / 2, row = L.rows.find(r => r.i === x_.i), ta = this.closeAt + (ord === 0 ? 0.15 : 0.8 + ord * 0.35);
          Zn.string(row.anchor.x, row.anchor.y, cx, L.tagY - size * 0.75, { t0: ta, dur: 0.5, w: ord === 0 ? 5 : 3.5 });
          tag(x_.tg, cx, L.tagY, size, { s: pop(T, ta + 0.4, 0.3, 2), rot: (k - 1) * 0.02, seed: 740 + k });
          x += ws[k] + gap;
        });
      }
      const word = R.verdict.reliable ? 'RELIABLE' : 'UNRELIABLE';
      const sw = docked ? 0.72 : 0.6, sx = X + W / 2 - (docked ? 300 : 250);
      Zn.stamp(word, sx, top + 46, { rot: -0.07, s: pop(T, this.closeAt + 0.9, 0.3) * sw, color: R.verdict.reliable ? GREEN : RED });
    }

    /* ---------------- the right-hand panel (desktop / landscape) ---------------- */
    panel() { const X = this.lay(false).panelX, W = 1892 - X, hs0 = UI.hudSlot(0); return { X, W, top: Math.max(128, hs0.y + hs0.r + 20) }; }
    card(x, top, w, h, seed) { Z().page(x + w / 2, top + h / 2, w, q10(h), { seed, rot: 0.004, dark: 0.25, tape: 1 }); }
    trustLayout() {
      const { X, W, top } = this.panel(), R = this.R, q = R.trust, pad = 24;
      const last = this.picks[this.picks.length - 1], why = last != null && last !== q.answerIndex ? q.why[last] : '';
      let C;
      for (const k of [1, 0.9, 0.8, 0.7, 0.62]) {
        const hs = UI.text(26) * k, qs = UI.text(34) * k, os = UI.text(30) * k, ws = UI.text(28) * k;
        const qL = wrapF(q.prompt, W - pad * 2, fnt(qs, TYPE));
        const opts = q.options.map((o, i) => { const lines = wrapF(o.label, W - pad * 2 - 64, fnt(os, TYPE)); return { i, lines, h: Math.max(UI.tap(84) + 4, lines.length * os * 1.25 + 26) }; });
        const whyL = why ? wrapF(why, W - pad * 2, fnt(ws, HAND)) : [];
        const head = 22 + hs * 1.4 + qL.length * qs * 1.25 + 14;
        const h = head + opts.reduce((a, o) => a + o.h, 0) + 16 + (whyL.length ? whyL.length * ws * 1.2 + 16 : 0);
        C = { X, W, pad, top, qL, opts, whyL, hs, qs, os, ws, head, h, last };
        if (top + h <= 1070) break;
      }
      return C;
    }
    drawTrust() {
      const R = this.R, q = R.trust, C = this.trustLayout(), { X, W, pad, top, qL, opts, whyL, hs, qs, os, ws, head, h } = C, t0 = this.caseAt + DROP;
      if (T < t0 - 0.2) return;
      this.card(X, top, W, h, 560);
      Z().type(`CASE ${this.bi + 1} OF ${this.n} · ${this.stars} OF ${this.of} STARS`, X + pad, top + 20 + hs * 0.6, hs, { color: GREY, seed: 561 });
      const qy = top + 22 + hs * 1.4;
      qL.forEach((l, i) => Z().type(l, X + pad, qy + (i + 0.5) * qs * 1.25, qs, { t0, cps: 60, seed: 565 + i }));
      let y = top + head;
      opts.forEach(o => {
        const i = o.i, cy = y + o.h / 2, crossed = this.picks.includes(i);
        g.save(); g.fillStyle = 'rgba(20,18,16,0.15)'; g.fillRect(X + 14, y + o.h - 1, W - 28, 2); g.restore();
        const hit = !crossed && this.phase === 'trust' ? UI._hit('cf-opt' + this.bi + '_' + i, X + W / 2, cy, W - 20, o.h) : { hover: false, clicked: false };
        if (hit.hover) { g.save(); g.globalAlpha *= 0.35; g.fillStyle = YEL; g.fillRect(X + 12, y + 5, W - 24, o.h - 10); g.restore(); }
        const kx = X + pad + 20, id = q.options[i].id;
        Z().mark(id, kx - 22, cy, 44, 44, { prog: 1, seed: 580 + i, alpha: crossed ? 0.3 : 0.75 });
        g.save(); g.lineWidth = 3; g.strokeStyle = INK; g.strokeRect(kx - 19, cy - 19, 38, 38);
        g.font = fnt(24, TYPE); g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), kx, cy + 1); g.restore();
        o.lines.forEach((l, k) => Z().type(l, kx + 40, cy + (k - (o.lines.length - 1) / 2) * os * 1.25, os, { t0: t0 + 0.3 + i * 0.1, cps: 90, color: crossed ? GREY : INK, seed: 575 + i * 3 + k }));
        const x1 = kx + 40 + Math.max(...o.lines.map(l => Z().measure(l, os)));
        if (crossed) ink([[kx + 30, cy + 10], [x1 + 10, cy - 10]], { w: 5, color: RED, seed: 590 + i, prog: i === C.last ? prog(this.pickedAt, 0.2) : 1, passes: 1, step: 12 });
        if (hit.clicked) this.choose(i);
        y += o.h;
      });
      y += 16;
      whyL.forEach((l, k) => hand(l, X + pad, y + (k + 0.5) * ws * 1.2, ws, this.pickedAt + 0.2, { align: 'left', color: RED, seed: 595 + k, dur: 0.45 }));
    }
    drawTap() {
      const { X, W, top } = this.panel(), R = this.R, pad = 24, t0 = this.tapAt;
      let C;
      for (const k of [1, 0.9, 0.8, 0.7, 0.62]) {
        const hs = UI.text(26) * k, vs = UI.text(32) * k, qs = UI.text(34) * k, ns = UI.text(28) * k, ss = UI.text(30) * k;
        const vL = wrapF(`${this.trustStar ? 'Right! ' : 'The verdict: '}${verdictLabel(R)}.`, W - pad * 2, fnt(vs, HAND));
        const qL = wrapF(R.tap.prompt, W - pad * 2, fnt(qs, TYPE));
        const nL = wrapF(UI.touch() ? 'Tap it on the case file.' : UI.hint('Click it on the case file (or press its number).', 'Tap it on the case file.'), W - pad * 2, fnt(ns, TYPE));
        const sL = this.say ? wrapF(this.say.text, W - pad * 2, fnt(ss, HAND)) : [];
        const h = 22 + hs * 1.4 + vL.length * vs * 1.2 + 12 + qL.length * qs * 1.25 + 10 + nL.length * ns * 1.3 + 18 + (sL.length ? sL.length * ss * 1.2 + 16 : 0);
        C = { hs, vs, qs, ns, ss, vL, qL, nL, sL, h };
        if (top + h <= 1070) break;
      }
      const { hs, vs, qs, ns, ss, vL, qL, nL, sL, h } = C;
      this.card(X, top, W, h, 570);
      Z().type(`CASE ${this.bi + 1} OF ${this.n} · ${this.stars} OF ${this.of} STARS`, X + pad, top + 20 + hs * 0.6, hs, { color: GREY, seed: 571 });
      let y = top + 22 + hs * 1.4;
      vL.forEach((l, i) => hand(l, X + pad, y + (i + 0.5) * vs * 1.2, vs, this.pickedAt || 0, { align: 'left', color: GREEN, seed: 572 + i, dur: 0.45 })); y += vL.length * vs * 1.2 + 12;
      qL.forEach((l, i) => Z().type(l, X + pad, y + (i + 0.5) * qs * 1.25, qs, { t0, cps: 60, seed: 575 + i })); y += qL.length * qs * 1.25 + 10;
      nL.forEach((l, i) => Z().type(l, X + pad, y + (i + 0.5) * ns * 1.3, ns, { color: GREY, seed: 580 + i })); y += nL.length * ns * 1.3 + 16;
      if (sL.length) { g.save(); g.fillStyle = 'rgba(217,53,44,0.25)'; g.fillRect(X + pad, y - 8, W - pad * 2, 2); g.restore(); }
      sL.forEach((l, i) => hand(l, X + pad, y + (i + 0.5) * ss * 1.2, ss, this.say.at + i * 0.15, { align: 'left', color: RED, seed: 585 + i, dur: 0.45 }));
    }
    closeLines() {
      const R = this.R, res = this.results[this.bi] || { stars: 0, trustStar: 0, tapStar: 0 }, i = this.hitI, out = [];
      out.push(['head', verdictLabel(R).toUpperCase()]);
      if (R.verdict.reliable) out.push(['clue', `Sentence ${i + 1}: the narrator can't know ${tagOf(R, i).sub}.`]);
      else { const c = Narration.CLUES[R.tap.clueOf[i]]; out.push(['clue', `Sentence ${i + 1}: ${c.name}. ${c.ask}`]); }
      if (this.revealed) out.push(['park', 'Ms. Park pinned that one for you.']);
      out.push(['note', `Ms. Park: ${R.explain}`]);
      out.push(['stars', `+${res.stars} star${res.stars === 1 ? '' : 's'} (verdict ${res.trustStar}, clue ${res.tapStar})`]);
      return out;
    }
    closeCard(L) {
      const key = this.bi + '|' + Math.round(UI.text(28));
      if (this._cc && this._cc.key === key) return this._cc;
      const { X, W, top } = this.panel(), F = this.closeLines(), bh = UI.tap(88), pad = 24, R = this.R;
      const pins = pinned(R, this.hitI);
      let C;
      for (const k of [1, 0.9, 0.8, 0.72, 0.64, 0.56]) {
        const tagS = Math.round(UI.text(30) * k), tagGap = tagS * 1.5 + 22, subS = UI.text(28) * k;
        const tags = pins.map(i => tagOf(R, i)), sub = [];
        const tagsH = tags.length * tagGap + sub.length * subS * 1.2 + 10;
        const S = { head: [UI.text(30) * k, TYPE], clue: [UI.text(28) * k, TYPE], park: [UI.text(28) * k, HAND], note: [UI.text(32) * k, HAND], stars: [UI.text(30) * k, HAND] };
        const lh = kk => S[kk][0] * 1.28;
        const rows = F.flatMap(([kk, t]) => wrapF(t, W - pad * 2, fnt(S[kk][0], S[kk][1])).map(l => [kk, l]));
        const h = rows.reduce((a, [kk]) => a + lh(kk), 0) + 30 + 3 * 8;
        const tagsTop = top + q10(h) + 16;                               // Ms. Park's card on top, the tags under it
        C = { key, X, W, pad, rows, h, S, lh, bh, top, tags, pins, tagS, tagGap, sub, subS, tagsH, cardTop: top, tagsTop, by: Math.min(1080 - bh / 2 - 6, tagsTop + tagsH + 8 + bh / 2) };
        if (tagsTop + tagsH + 8 + bh + 6 <= 1080) break;
      }
      return (this._cc = C);
    }
    drawClose(L) {
      const C = this.closeCard(L), { X, W, pad, rows, h, S, lh, bh, tags, pins, tagS, tagGap, cardTop, tagsTop, by } = C;
      // the evidence tags, pinned in a column, a red string from each sentence
      tags.forEach((tg, k) => {
        const ord = this.pinOrder(pins, pins[k]), w = tagW(tg, tagS), cy = tagsTop + tagS * 0.75 + 6 + k * tagGap, cx = X + 26 + w / 2, ta = this.closeAt + (ord === 0 ? 0.15 : 0.8 + ord * 0.35);
        const row = L.rows.find(r => r.i === pins[k]);
        tag(tg, cx, cy, tagS, { s: pop(T, ta + 0.4, 0.3, 2), rot: (k % 2 ? 0.015 : -0.015), seed: 740 + k });
        Z().string(row.anchor.x, row.anchor.y, X + 22, cy, { t0: ta, dur: 0.5, w: ord === 0 ? 5 : 3.5 });
      });
      if (T < this.closeAt + 0.6) return;
      this.card(X, cardTop, W, h, 600);
      let y = cardTop + 18, tt = this.closeAt + 0.6, prev = null;
      const R = this.R;
      rows.forEach(([k, l], j) => {
        if (prev && prev !== k) y += 8; prev = k;
        const s = S[k][0], cy = y + lh(k) / 2;
        if (k === 'head') Z().type(l, X + pad, cy, s, { t0: tt, cps: 50, color: R.verdict.reliable ? GREEN : RED, seed: 601 + j });
        else if (k === 'clue') Z().type(l, X + pad, cy, s, { t0: tt + 0.4, cps: 80, color: INK, seed: 610 + j });
        else if (k === 'park') hand(l, X + pad, cy, s, tt + 0.6, { align: 'left', color: PARK, seed: 620 + j, dur: 0.45 });
        else if (k === 'note') hand(l, X + pad, cy, s, tt + 0.9 + j * 0.05, { align: 'left', color: PARK, seed: 630 + j, dur: 0.45 });
        else hand(l, X + pad, cy, s, tt + 1.3, { align: 'left', color: RED, seed: 640 + j, dur: 0.45 });
        y += lh(k);
      });
      const label = this.bi < this.n - 1 ? 'Next case ▶' : 'See my stars ▶';
      if (Game.time - this.closeAt > NEXT_GUARD && UI.button('cf-next', label, X + W / 2, by, { w: Math.min(W - 30, 420), h: 88 })) this.next();
    }

    /* ---------------- portrait: the case card is the picture; everything else in the dock ---------------- */
    drawDock() {
      const R = this.R, id = this.attempt + '_' + this.bi, q = R.trust;
      Dock.head(`Case ${this.bi + 1} of ${this.n}: ${R.title}`, `${this.stars} of ${this.of} stars`);
      const sents = R.sentences.map(s => s.text);
      if (this.phase === 'drop' || this.phase === 'trust') {
        Dock.text('cf-src' + id, [sourceLine(R)], { cls: 'small' });
        if (this.phase === 'trust') Dock.choices('cf-trust' + id, { prompt: q.prompt, options: q.options.map(o => o.label), state: q.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : '')),
          notes: q.options.map((o, i) => (this.picks.includes(i) ? q.why[i] : '')), onChoose: i => this.choose(i) });
        return;
      }
      if (this.phase === 'tap') {
        Dock.text('cf-verdict' + id, [`${this.trustStar ? 'Right! ' : 'The verdict: '}${verdictLabel(R)}.`], { cls: 'right' });
        Dock.choices('cf-tap' + id, { prompt: R.tap.prompt, options: sents, state: sents.map((s, i) => (this.tapMarks[i] ? 'wrong' : '')),
          notes: sents.map((s, i) => (this.tapMarks[i] ? R.tap.why(i) : '')), onChoose: i => this.tap(i) });
        return;
      }
      // close
      const pins = pinned(R, this.hitI), F = this.closeLines();
      Dock.heading('cf-close' + id, F[0][1]);
      Dock.choices('cf-tap' + id, { prompt: R.tap.prompt, options: sents,
        state: sents.map((s, i) => (pins.includes(i) ? 'right' : this.tapMarks[i] ? 'wrong' : 'faded')),
        notes: sents.map((s, i) => (pins.includes(i) ? tagOf(R, i).text + (tagOf(R, i).sub ? ': ' + tagOf(R, i).sub : '') : '')), locked: true });
      Dock.text('cf-clue' + id, F.filter(x => x[0] === 'clue' || x[0] === 'park').map(x => x[1]));
      Dock.text('cf-note' + id, [`Ms. Park: ${R.explain}`], { cls: 'note' });
      Dock.text('cf-stars' + id, [F[F.length - 1][1]], { cls: 'right', reveal: true });
      Dock.button({ id: 'next', glyph: this.bi < this.n - 1 ? 'Next case ▶' : 'See my stars ▶', label: 'next', aria: 'next case', go: true, onClick: () => this.next() });
    }

    drawIntro(docked) {
      const Zn = Z(), t0 = this.enteredAt, n = this.n, pass = this.passStars, of = this.of;
      const lines = [`${n} case files. In each one, a narrator tells you what happened.`, 'First: can you trust this narrator? Then tap the sentence that proves it.',
        `1 star for the verdict, 1 for the clue. ${pass} of ${of} stars for the stamp.`];
      const rise = E.outBack(P(T, t0 + 0.2, t0 + 0.7), 1.4);
      if (docked) {
        Zn.card(1080, 540, 1500, 900, { seed: 77, rot: 0.01, lines: false });
        Zn.highlight(1080 - 320, 320, 640, 100, YEL, { t0: t0 + 0.5, seed: 78 });
        Zn.type('CASE FILES', 1080, 320, 110, { t0: t0 + 0.1, cps: 30, align: 'center', seed: 79 });
        tag({ id: 'something-to-gain', text: 'SOMETHING TO GAIN' }, 1080, 560, 56, { s: pop(T, t0 + 0.6), rot: -0.03, seed: 80 });
        tag({ id: 'missing-info', text: 'MISSING INFO' }, 1200, 740, 56, { s: pop(T, t0 + 0.8), rot: 0.03, seed: 81 });
        Puppet.draw('park', 250, 1080 + (1 - rise) * 500, 760, { facing: 1, t: T });
        Dock.heading('cf-intro', 'Case Files', { cls: 'big' });
        Dock.text('cf-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Open the case files', go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      Puppet.draw('park', 300, 1080 + (1 - rise) * 500, 720, { facing: 1, t: T });
      Zn.card(1160, 590, 1340, 900, { seed: 77, rot: 0.006, lines: false });
      Zn.highlight(1160 - 270, 290, 540, 90, YEL, { t0: t0 + 0.5, seed: 78 });
      Zn.type('CASE FILES', 1160, 290, 96, { t0: t0 + 0.1, cps: 30, align: 'center', seed: 79 });
      Zn.type('every narrator is a witness: check the story', 1160, 362, 30, { align: 'center', color: GREY, seed: 82 });
      const fs = UI.text(32);
      let y = 440;
      lines.forEach((l, i) => wrapF(l, 1140, fnt(fs, TYPE)).forEach(t => { Zn.type(t, 1160, y, fs, { t0: t0 + 0.4 + i * 0.25, cps: 80, align: 'center', seed: 83 + i, color: i === 2 ? RED : INK }); y += fs * 1.45; }));
      y += 30;
      const ts = UI.text(30);
      const a = { id: 'something-to-gain', text: 'SOMETHING TO GAIN' }, b = { id: 'says-vs-does', text: 'SAYS vs DOES' };
      const ax = 900, bx = 1420, ty = y + 40;
      Zn.string(ax + tagW(a, ts) / 2 - 20, ty, bx - tagW(b, ts) / 2 + 20, ty + 20, { t0: t0 + 1.4, dur: 0.5, w: 4 });
      tag(a, ax, ty, ts, { rot: -0.04, s: pop(T, t0 + 1.2), seed: 80 });
      tag(b, bx, ty + 20, ts, { rot: 0.04, s: pop(T, t0 + 1.35), seed: 81 });
      const by = Math.min(960, ty + 170);
      if (this.t > 0.8 && UI.button('cf-start', 'Open the files ▶', 1160, by, { w: 460, h: 104 })) this.begin();
      if (this.t > 1.2) hand(UI.hint('(or press Enter)', ''), 1160, by + 76, UI.text(28), 0, { dur: 0.01, color: GREY });
    }
    drawEnd(docked) {
      const Zn = Z(), s = this.stars, of = this.of, btns = this.endButtons();
      const rows = this.cases.map((c, i) => { const r = this.results[i] || { stars: 0 }; return { r, c, text: `${i + 1}. ${c.title}`, v: c.verdict.reliable ? 'reliable' : 'unreliable' }; });
      const word = this.passed ? 'CASE CLOSED' : 'REOPENED';
      if (docked) {
        Zn.card(960, 540, 1500, 900, { seed: 91, rot: -0.008, lines: false });
        Zn.type(`${s} / ${of}`, 960, 400, 200, { t0: this.endAt + 0.1, cps: 20, align: 'center', seed: 92 });
        Zn.stamp(word, 960, 720, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.4 });
        Dock.heading('cf-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('cf-end', [this.passed ? 'Every case closed! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New cases?`]);
        Dock.list('cf-end', rows.map((x, i) => ({ badge: String(i + 1), color: x.r.stars === 2 ? GREEN : RED, lines: [`${x.c.title} (${x.v})`, `${x.r.stars} of 2 stars`] })));
        Dock.text('cf-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('cf-end', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      Zn.card(960, 600, 1500, 900, { seed: 91, rot: -0.004, lines: false });
      Zn.type(`${s} / ${of}`, 700, 260, 120, { t0: this.endAt + 0.1, cps: 20, align: 'center', seed: 92 });
      Zn.stamp(word, 1300, 260, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 0.9 });
      const fs = UI.text(32), rh = Math.max(64, fs * 1.45), bh = UI.tap(100);
      const top = Math.min(390, 1080 - 40 - bh - 90 - rows.length * rh);
      rows.forEach((x, i) => {
        const y = top + i * rh;
        Zn.type(x.text, 320, y, fs, { t0: this.endAt + 0.3 + i * 0.1, cps: 90, seed: 93 + i });
        Zn.type(x.v, 1130, y, fs * 0.8, { align: 'right', color: GREY, seed: 94 + i });
        hand(`${x.r.stars} of 2`, 1560, y, fs * 1.2, this.endAt + 0.4 + i * 0.1, { align: 'right', color: x.r.stars === 2 ? GREEN : RED, seed: 100 + i });
        if (x.r.stars === 2) tick(1610, y, 0.9, { prog: prog(this.endAt + 0.5 + i * 0.1, 0.25), seed: 110 + i });
      });
      const py = top + rows.length * rh + 10, by = Math.min(1080 - 30 - bh / 2, py + 50 + bh / 2);
      hand(this.passed ? 'Every case closed! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp.`, 960, py, UI.text(36), this.endAt + 0.8, { color: this.passed ? PARK : RED });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('cf-end' + i, b.label, 700 + i * 520, by, { w: 470, h: 100, color: i === 0 ? RED : YEL, text: i === 0 ? '#fffefa' : INK })) b.go(); });
      Zn.type(`quiz code ${this.seed}`, 1690, by + bh / 2 + 18 > 1068 ? 1060 : by + bh / 2 + 18, UI.text(20), { align: 'right', color: GREY, seed: 120 });
    }
  }
  CaseFiles.STEP = STEP; CaseFiles.layCase = layCase;
  Object.defineProperty(CaseFiles, 'solution', { get() { const s = Game.current(); return s instanceof CaseFiles ? s.solution : null; } });
  /* the live canvas tap rects (tests tap them): sentences (Part B) + options (Part A), centred {i, kind, x, y, w, h} */
  CaseFiles.targets = () => {
    const s = Game.current();
    if (!(s instanceof CaseFiles) || !s.R || UI.docked()) return [];
    const L = s.lay(false);
    const sent = L.rows.map(r => ({ i: r.i, kind: 'sentence', ...r.hit }));
    const opts = UI.hitRects.filter(h => h.id.startsWith('cf-opt' + s.bi + '_')).map(h => ({ i: +h.id.split('_').pop(), kind: 'option', x: h.x, y: h.y, w: h.w, h: h.h }));
    return sent.concat(opts);
  };
  Levels.cases = CaseFiles;
})();
